import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';

const VIEWPORT_PAD = 8;

function computeCi2PopoverLayout(anchorRect, popoverHeight, width, maxHeightCap = 420) {
  let left = anchorRect.left;
  const gap = 8;
  let top = anchorRect.bottom + gap;
  let flipped = false;

  if (left + width > window.innerWidth - VIEWPORT_PAD) {
    left = window.innerWidth - width - VIEWPORT_PAD;
  }
  if (left < VIEWPORT_PAD) left = VIEWPORT_PAD;

  const spaceBelow = window.innerHeight - anchorRect.bottom - VIEWPORT_PAD;
  const estimatedHeight = popoverHeight || 280;

  if (spaceBelow < estimatedHeight + gap && anchorRect.top > estimatedHeight + gap) {
    flipped = true;
    top = anchorRect.top - estimatedHeight - gap;
  }

  const maxTop = window.innerHeight - estimatedHeight - VIEWPORT_PAD;
  if (top > maxTop) top = Math.max(VIEWPORT_PAD, maxTop);
  if (top < VIEWPORT_PAD) top = VIEWPORT_PAD;

  const available = flipped
    ? anchorRect.top - VIEWPORT_PAD - gap
    : window.innerHeight - anchorRect.bottom - VIEWPORT_PAD - gap;
  const cap = Math.min(maxHeightCap, window.innerHeight - VIEWPORT_PAD * 2);
  const maxHeight = Math.max(180, Math.min(cap, available));

  const anchorCenterX = anchorRect.left + anchorRect.width / 2;
  const arrowLeft = Math.min(Math.max(anchorCenterX - left, 14), width - 14);

  return {
    position: 'fixed',
    top,
    left,
    width,
    boxSizing: 'border-box',
    zIndex: 12000,
    maxHeight,
    flipped,
    arrowLeft,
  };
}

const DEFAULT_ESTIMATED_HEIGHT = 280;

export function Ci2EditPopover({
  open,
  anchorRef,
  onClose,
  width = 320,
  maxHeight = 420,
  estimatedHeight = DEFAULT_ESTIMATED_HEIGHT,
  children,
  className = '',
  title,
  initialFocusRef,
}) {
  const popRef = useRef(null);
  const scrollLockRef = useRef(null);
  const [layout, setLayout] = useState(null);

  const updateLayout = () => {
    const anchor = anchorRef?.current;
    if (!open || !anchor) {
      setLayout(null);
      return;
    }
    const rect = anchor.getBoundingClientRect();
    const popHeight = popRef.current?.offsetHeight || estimatedHeight;
    setLayout(computeCi2PopoverLayout(rect, popHeight, width, maxHeight));
  };

  const restoreScroll = () => {
    const lock = scrollLockRef.current;
    if (!lock) return;
    if (window.scrollX !== lock.x || window.scrollY !== lock.y) {
      window.scrollTo(lock.x, lock.y);
    }
  };

  useLayoutEffect(() => {
    if (!open) {
      scrollLockRef.current = null;
      setLayout(null);
      return;
    }
    scrollLockRef.current = { x: window.scrollX, y: window.scrollY };
    updateLayout();
    restoreScroll();
  }, [open, width, maxHeight, estimatedHeight, anchorRef]);

  useLayoutEffect(() => {
    if (!open || !popRef.current) return;
    updateLayout();
    restoreScroll();
  }, [open, children]);

  useEffect(() => {
    if (!open) return undefined;
    const onResize = () => {
      updateLayout();
      restoreScroll();
    };
    const onWindowScroll = () => restoreScroll();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onWindowScroll);
    document.addEventListener('scroll', onWindowScroll, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onWindowScroll);
      document.removeEventListener('scroll', onWindowScroll, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  useEffect(() => {
    if (!open) return undefined;
    const fn = (e) => {
      const pop = popRef.current;
      const anchor = anchorRef?.current;
      if (pop?.contains(e.target) || anchor?.contains(e.target)) return;
      if (e.target.closest('.defeitos-vv-sev-dropdown')) return;
      onClose();
    };
    document.addEventListener('mousedown', fn, true);
    return () => document.removeEventListener('mousedown', fn, true);
  }, [open, onClose, anchorRef]);

  useEffect(() => {
    if (!open || !initialFocusRef?.current) return undefined;
    const id = window.setTimeout(() => {
      initialFocusRef.current?.focus({ preventScroll: true });
      restoreScroll();
    }, 0);
    return () => window.clearTimeout(id);
  }, [open, initialFocusRef]);

  if (!open) return null;

  const anchorRect = anchorRef?.current?.getBoundingClientRect();
  const fallbackLayout = anchorRect
    ? computeCi2PopoverLayout(anchorRect, estimatedHeight, width, maxHeight)
    : {
        position: 'fixed',
        top: VIEWPORT_PAD,
        left: VIEWPORT_PAD,
        width,
        boxSizing: 'border-box',
        zIndex: 12000,
        maxHeight,
      };

  const resolvedLayout = layout || fallbackLayout;
  const arrowLeft = resolvedLayout.arrowLeft ?? width / 2;
  const flipped = !!resolvedLayout.flipped;

  const style = {
    position: resolvedLayout.position,
    top: resolvedLayout.top,
    left: resolvedLayout.left,
    width: resolvedLayout.width,
    boxSizing: resolvedLayout.boxSizing,
    zIndex: resolvedLayout.zIndex,
    maxHeight: resolvedLayout.maxHeight,
    '--ci2-popover-arrow-left': `${arrowLeft}px`,
  };

  return ReactDOM.createPortal(
    <div
      ref={popRef}
      className={`ci2-edit-popover ci2-edit-popover--with-arrow${
        flipped ? ' ci2-edit-popover--flipped' : ''
      }${className ? ` ${className}` : ''}`}
      style={style}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <span className="ci2-edit-popover-arrow" aria-hidden />
      <div className="ci2-edit-popover-shell">{children}</div>
    </div>,
    document.body
  );
}
export function Ci2EditPopoverActions({ onCancel, onConfirm, confirmDisabled, saving, confirmTitle }) {
  return (
    <div className="ci2-edit-popover-foot">
      <button type="button" className="ci2-pop-btn ci2-pop-btn-ghost" onClick={onCancel} disabled={saving}>
        Cancelar
      </button>
      <button
        type="button"
        className="ci2-pop-btn ci2-pop-btn-primary"
        onClick={onConfirm}
        disabled={confirmDisabled || saving}
        title={confirmTitle}
      >
        {saving ? <span className="condicoes-spinner-tiny" /> : 'Confirmar'}
      </button>
    </div>
  );
}
